import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";

const links = [
  { label: "Home", id: "hero" },
  { label: "Collections", id: "collections" },
  { label: "Sale", id: "sale" },
  { label: "Videos", id: "showroom" },
  { label: "Why Us", id: "manifesto" },
  { label: "Visit", id: "contact" },
];

export const Header = ({ business, logo }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1] }}
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-black/85 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
        <button onClick={() => go("hero")} data-testid="header-logo-btn" className="flex items-center gap-3">
          <img src={logo} alt={business.name} className="h-11 w-auto object-contain" />
          <span className="hidden sm:block font-display text-lg leading-none tracking-wide metallic-text">
            {business.name}
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => go(l.id)}
              className="text-sm font-medium uppercase tracking-widest text-white/70 hover:text-white transition-colors relative group"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-ad-red group-hover:w-full transition-all duration-300" />
            </button>
          ))}
        </nav>

        <a
          href={`tel:${(business.phones?.[0] || "").replace(/[^0-9]/g, "")}`}
          data-testid="header-call-btn"
          className="hidden md:flex items-center gap-2 px-5 py-2.5 bg-ad-red text-white text-sm font-bold uppercase tracking-wider hover:bg-ad-redhover transition-colors"
        >
          <Phone size={15} />
          {business.phones?.[0]}
        </a>

        <button data-testid="mobile-menu-btn" className="md:hidden text-white" onClick={() => setOpen(!open)}>
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-black/95 backdrop-blur-xl border-b border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-nav-${l.id}`}
                  onClick={() => go(l.id)}
                  className="text-left font-condensed text-2xl tracking-wide text-white/80"
                >
                  {l.label}
                </button>
              ))}
              <a
                href={`tel:${(business.phones?.[0] || "").replace(/[^0-9]/g, "")}`}
                className="mt-2 flex items-center justify-center gap-2 px-5 py-3 bg-ad-red text-white font-bold uppercase tracking-wider"
              >
                <Phone size={16} /> Call {business.phones?.[0]}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
